import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';
import Head from 'next/head';
import cn from 'classnames';
import { getProductInfo, viewItemList, selectPromotion, bannersAnalyticsFunction } from '@/utils/analytics.js';
import { useRouter } from 'next/router';
import { WaveBreadCrumb, HomePageBanner } from '@desquared/wave-components-library';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import CategoriesAisles from '@/components/CategoriesAisles/CategoriesAisles';
import PageLayout from '@/components/PageLayout';
import OffersCategoriesMenu from '@/components/OffersCategoriesMenu/OffersCategoriesMenu';
import DefaultProductImage from '../../../public/assets/wave-grocery/Empty-States-Default/DefaultProductImage.png';

/* Controllers */
import { wave } from '@/api/config';
import { getV31Products } from '@/api/Products';
import { getCmsHomepageLayout } from '@/api/cms.js';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { productsForOrder } from '@/store/cart';
import { getCategoryMetadata, sortByIsFeaturedAndCategoryPosition } from '@/utils/category';
import { getLayoutProps } from '@/utils/layout/layout';
import { getCategoriesFromFile } from '@/utils/getCategories';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import { createCategoriesForBundle } from '@/utils/category';
import { createOfferCategories } from '@/utils/trimmCategories';
import { getOrFetchOrderLimitations } from '@/utils/orderLimitations';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import { useVisibleCategories } from '@/contexts/VisibleCategoriesContext';
import { transformCategoryAisles, replaceUndefinedWithNull } from '@/utils/categoryAisles';

import styles from '@/styles/Categories.module.css';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { setBanner } from '@/utils/homePageCmsCall.js';
const CATEGORIES_NUMBER_TO_PREFETCH = 5;

export default function Offers({
  responsiveLogo,
  allCategories,
  productsPerCategoryInitial,
  privateApiKey,
  emptyStates,
  imageBanners,
}) {
  const observer = useRef(null);
  const isMobile = useWindowUnderThreshold(850);
  const router = useRouter();
  const t = useTranslations();
  const a = useTranslations('common.asides');

  const { getCollectionTypes, selectedService } = useContext(DeliveryServicesContext);
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();

  const defaultProdImg = emptyStates?.default_product_image?.url || DefaultProductImage.src;
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const { visibleCategories } = useVisibleCategories();

  const transformedImageBanners = useMemo(() => {
    return setBanner(imageBanners);
  }, [imageBanners]);

  const breadCrumbItems = useMemo(
    () => [
      {
        title: t('offers.breadcrumb_title'),
        link: '/offers',
      },
    ],
    [t]
  );

  const {
    data,
    isLoading: isLoadingAisles,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['offer-aisles', router.locale, getCollectionTypes(), selectedService],
    enabled: !!selectedService,
    queryFn: async ({ pageParam }) => {
      return await Promise.all(
        allCategories
          .filter(
            (_, index) =>
              index >= pageParam * CATEGORIES_NUMBER_TO_PREFETCH &&
              index < (pageParam + 1) * CATEGORIES_NUMBER_TO_PREFETCH
          )
          .map(category =>
            getV31Products(
              { category: category._id, collectionTypes: getCollectionTypes(), pageSize: 10, withAnyOffer: true },
              router.locale,
              privateApiKey
            )
          )
      );
    },
    getNextPageParam: (_, allPages) => {
      if (allPages.length >= Math.ceil(allCategories.length / CATEGORIES_NUMBER_TO_PREFETCH)) return undefined;

      return allPages.length;
    },
    initialPageParam: 0,
    initialData: { pages: [productsPerCategoryInitial], pageParams: [0] },
    staleTime: 0,
  });

  const { data: limitations } = useQuery({
    queryKey: ['limitations', getCollectionTypes() || []],
    queryFn: getOrFetchOrderLimitations,
  });

  useEffect(() => {
    if (!data) return;

    const lastPage = data.pages[data.pages.length - 1];

    // Send event for items from last load
    const analyticsItems = lastPage.flatMap(aisle =>
      aisle.map((product, index) => getProductInfo(product, { index: index + 1 }, 'offers'))
    );

    viewItemList(analyticsItems, {
      item_list_id: 'offers',
      item_list_name: 'offers',
    });
  }, [data]);

  const lastAisleRef = useCallback(
    node => {
      if (!node) return;
      if (isLoadingAisles || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isLoadingAisles, isFetchingNextPage, fetchNextPage]
  );

  const bundleCategories = useMemo(() => {
    return createCategoriesForBundle(allCategories, undefined, visibleCategories);
  }, [allCategories, router.asPath, visibleCategories]);

  //handles the user clicking on a category from the menu
  const onCategoriesClick = useCallback(
    id => {
      const newCategory = getCategoryMetadata(id, router.locale);

      if (newCategory) {
        router.push(`/${newCategory?.fullSlug.replace('categories', 'offers')}?withOffer=true&withBundleOffers=true`);
      }
    },
    [router]
  );
  const selectedCategory = {};
  selectedCategory.name = t('offers.title');
  if (bundleCategories.length === 0) return <div style={{ height: '100vh' }} />;
  return (
    <>
      <Head>
        <title>{process.env.displayName}</title>
        <meta name="description" content={`App for ${process.env.displayName} by Wave Grocery`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer}>
        {isMobile ? (
          <div className={styles.breadCrumbContainer}>
            <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} lastElementClickable />
          </div>
        ) : null}
        <div className={styles.categoriesWrapper}>
          <div className={styles.categoriesContainer}>
            <div className={styles.categoriesInnerContainer}>
              <OffersCategoriesMenu
                onCategoriesClick={onCategoriesClick}
                bundleCategories={bundleCategories}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>
          <div className={styles.productsContainer}>
            {isMobile ? null : (
              <div className={styles.breadCrumbContainer}>
                <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} lastElementClickable />
              </div>
            )}

            {isMobile ? null : (
              <div className={styles.titleContainer}>
                <CategoryTitle title={t('offers.title')} subtitle={t('offers.subtitle')} />
              </div>
            )}
            {isMobile ? (
              <>
                <ScrollToTop />
              </>
            ) : null}
            <div className={styles.aislesContainer}>
              {transformedImageBanners?.length > 0 && (
                <HomePageBanner
                  analyticsFunction={bannersAnalyticsFunction}
                  isFixedWidth={true}
                  images={transformedImageBanners}
                  refreshTime={1000000}
                  classOption={cn(styles.banner)}
                  LinkComponent={Link}
                  showPagination={false}
                  onClick={index => {
                    selectPromotion({
                      creative_name: transformedImageBanners[index]?.altText ?? `offers_banner`,
                      creative_slot: transformedImageBanners[index]?.altText ?? `offers_banner`,
                      promotion_id: transformedImageBanners[index]?.altText ?? `offers_banner`,
                      promotion_name: transformedImageBanners[index]?.altText ?? `offers_banner`,
                    });
                  }}
                />
              )}

              <CategoriesAisles
                aisles={
                  data
                    ? sortByIsFeaturedAndCategoryPosition(
                        transformCategoryAisles(
                          allCategories,
                          data.pages.flatMap(page => page.map(aisle => aisle.map(enhanceProductWithSlug))),
                          productTags,
                          bundleOffersSummary,
                          personalizedProductsIds,
                          a
                        )
                      )
                    : []
                }
                hasPadding={true}
                limitations={limitations}
                tags={productTags}
                bundleOffersSummary={bundleOffersSummary}
                isOffers={true}
                isLoading={isLoadingAisles || isFetchingNextPage}
                lastAisleRef={lastAisleRef}
                defaultProductImage={defaultProdImg}
              />
            </div>
          </div>
        </div>
        <ScrollToTop />
      </main>
    </>
  );
}

Offers.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 1 } }, context.locale);
  const configuration = await getOrFetchConfiguration();
  const displayStrategies = configuration?.catalog?.offer?.categories?.displayStrategies;
  const categoriesFromFile = await getCategoriesFromFile(context.locale);
  const allCategories = createOfferCategories(categoriesFromFile, displayStrategies);
  async function getV31ProductsWithAPIKey(resource) {
    const response = await wave.get('/v3.1/products', {
      headers: { 'x-api-key': process.env.PRIVATE_API_KEY, 'Accept-Language': context.locale },
      params: {
        available: true,
        collectionTypes: [process.env.defaultCollectionType],
        pageNumber: 0,
        pageSize: 10,
        categorySearchType: 'ALL_LEVELS',
        withAnyOffer: true,
        ...resource,
      },
    });

    return response.data;
  }

  const mainCategory = layoutProps.categories.find(el => el.integratorId == -1 && el.friendlyId == -1);

  const { data: BannerData } = await getCmsHomepageLayout(mainCategory?.carousel_id);
  let imageBanners = [];
  if (BannerData.included?.homepage_banner_web_item?.length > 0 && mainCategory?.carousel_id) {
    imageBanners = BannerData.included.homepage_banner_web_item;
  }

  let productsPerCategory = [];
  try {
    productsPerCategory = await Promise.all(
      allCategories
        .filter((i, index) => index < CATEGORIES_NUMBER_TO_PREFETCH)
        .map(i => getV31ProductsWithAPIKey({ category: i._id, withAnyOffer: true }))
    );
  } catch (err) {
    console.error('>>>fetch offers error');
  }

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    categories: (await import(`../../../prebuild-data/i18n/${context.locale}/categories.json`)).default,
    offers: (await import(`../../../prebuild-data/i18n/${context.locale}/offers.json`)).default,
  };

  const latestCategories = replaceUndefinedWithNull(allCategories);

  return {
    props: {
      ...layoutProps,
      allCategories: latestCategories.map(i => {
        return {
          ...i,
          icon: i?.icon || null,
          selectedIcon: i?.selectedIcon || null,
          top_banners: i?.top_banners || null,
          side_banners: i?.side_banners || null,
          isFeatured: i?.isFeatured || null,
          position: i?.position || null,
        };
      }),
      productsPerCategoryInitial: productsPerCategory,
      privateApiKey: process.env.NODE_ENV === 'development' ? process.env.PRIVATE_API_KEY : null,
      categoriesFromFile,
      messages,
      imageBanners,
    },
  };
}
